import { useEffect, useState, useRef } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import * as THREE from 'three';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
const Model = ({ geometry }) => (
  <mesh geometry={geometry}>
    <meshStandardMaterial color="#abebffff" metalness={0.5} roughness={0.2} />
  </mesh>
);

const CameraController = ({ position, refreshFlag }) => {
  const { camera, gl } = useThree();
  const controlsRef = useRef();

  useEffect(() => {
    camera.position.set(position[0], position[1], position[2]);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();

    if (controlsRef.current) {
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
    }
  }, [position, camera, refreshFlag]);

  return <OrbitControls ref={controlsRef} args={[camera, gl.domElement]} enableRotate={true} />;
};

const STLViewer = ({ url }) => {
  const [geometry, setGeometry] = useState(null);
  const [error, setError] = useState(null);
  const views = ['isometric', 'front', 'top', 'side'];
  const [currentViewIndex, setCurrentViewIndex] = useState(1);
  const [refreshFlag, setRefreshFlag] = useState(false);

  useEffect(() => {
    const fetchSTL = async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const arrayBuffer = await res.arrayBuffer();
        const loader = new STLLoader();
        const geom = loader.parse(arrayBuffer);

        geom.computeBoundingBox();
        const center = new THREE.Vector3();
        geom.boundingBox.getCenter(center).negate();
        geom.translate(center.x, center.y, center.z);

        const size = new THREE.Vector3();
        geom.boundingBox.getSize(size);
        const scale = 50 / Math.max(size.x, size.y, size.z);
        geom.scale(scale, scale, scale);

        setGeometry(geom);
      } catch (err) {
        setError("Failed to load STL");
      }
    };

    fetchSTL();
  }, [url]);

  if (error) return <div>{error}</div>;
  if (!geometry) return <div>Loading STL...</div>;

  const cameraPositions = {
    isometric: [70, 70, 70],
    front: [0, 0, 100],
    top: [0, 100, 0],
    side: [100, 0, 0]
  };

  const refreshTopView = () => {
    setCurrentViewIndex(1);
    setRefreshFlag(prev => !prev);
  };

  const currentView = views[currentViewIndex];

  return (
    <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      {/* Header / Buttons */}
      <div style={{ marginBottom: 10, textAlign: "center" }}>
        <button
          onClick={refreshTopView}
          style={{
            padding: "8px 14px",
            fontSize: "16px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            background: "#007bff",
            color: "#fff"
          }}
        >
          🔄 Reset
        </button>
      </div>

      {/* RESPONSIVE CANVAS */}
      <div
        style={{
          width: "100%",
          height: "65vh",           // responsive height (mobile friendly)
          minHeight: "300px",
          borderRadius: "10px",
          overflow: "hidden"
        }}
      >
        <Canvas style={{ width: "100%", height: "100%" }}>
          <ambientLight intensity={0.6} />
          <directionalLight position={[100, 100, 100]} intensity={1} />
          <PerspectiveCamera makeDefault fov={45} />
          <CameraController position={cameraPositions[currentView]} refreshFlag={refreshFlag} />
          <Model geometry={geometry} />
        </Canvas>
      </div>
    </div>
  );
};

export default STLViewer;
